import { api } from "@/lib/api";
import { cn, formatCurrency, relativeTime } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, Link2, RefreshCw } from "lucide-react";
import { useState } from "react";
import { PipelineSection } from "./LexofficePipeline";
import {
	InvoicesSection,
	type OpenInvoice,
	UnlinkedSection,
	type Voucher,
} from "./LexofficeVouchers";

// ============================================================
// LexOffice Page — Vorgänge, Offene Rechnungen, Nicht Zugeordnet
// ============================================================

interface LexStats {
	total: number;
	unlinked: number;
	byType: Record<string, number>;
	lastSync: string | null;
}

type Tab = "pipeline" | "invoices" | "unlinked";

export function LexofficePage() {
	const queryClient = useQueryClient();
	const [activeTab, setActiveTab] = useState<Tab>("pipeline");

	// Queries
	const statsQuery = useQuery({
		queryKey: ["lexoffice", "stats"],
		queryFn: () => api.get<{ data: LexStats }>("/lexoffice/stats"),
	});

	const invoicesQuery = useQuery({
		queryKey: ["lexoffice", "open-invoices"],
		queryFn: () =>
			api.get<{
				data: OpenInvoice[];
				summary: { count: number; totalOpen: number; overdue: number };
			}>("/lexoffice/open-invoices"),
	});

	const unlinkedQuery = useQuery({
		queryKey: ["lexoffice", "vouchers", "unlinked"],
		queryFn: () =>
			api.get<{ data: Voucher[]; meta: { total: number } }>(
				"/lexoffice/vouchers?unlinked=true&limit=100",
			),
	});

	// Mutations
	const syncMutation = useMutation({
		mutationFn: () => api.post("/lexoffice/sync"),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["lexoffice"] });
		},
	});

	const relinkMutation = useMutation({
		mutationFn: () =>
			api.post<{
				data: { ok: boolean; relinked: number; total: number; message: string };
			}>("/settings/sync/lexoffice/relink"),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["lexoffice"] });
		},
	});

	// Derived data
	const stats = statsQuery.data?.data;
	const openInvoices = invoicesQuery.data?.data ?? [];
	const summary = invoicesQuery.data?.summary;
	const unlinked = unlinkedQuery.data?.data ?? [];

	const unlinkedTotal = unlinkedQuery.data?.meta?.total ?? unlinked.length;
	const invoiceTotal = summary?.count ?? openInvoices.length;

	const tabs: [Tab, string][] = [
		["pipeline", "Vorgänge"],
		["invoices", `Offene Rechnungen (${invoiceTotal})`],
		["unlinked", `Nicht zugeordnet (${unlinkedTotal})`],
	];

	return (
		<div className="space-y-6 animate-fade-in">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="font-display text-2xl">LexOffice</h1>
					{stats?.lastSync && (
						<p className="text-xs text-stone-400 mt-0.5">
							Letzter Sync: {relativeTime(stats.lastSync)}
						</p>
					)}
				</div>
				<div className="flex items-center gap-2">
					{(stats?.unlinked ?? 0) > 0 && (
						<button
							type="button"
							onClick={() => relinkMutation.mutate()}
							disabled={relinkMutation.isPending}
							className="btn-secondary"
						>
							<Link2
								size={14}
								className={cn(relinkMutation.isPending && "animate-spin")}
							/>
							{relinkMutation.isPending
								? "Zuordnung läuft..."
								: "Belege neu zuordnen"}
						</button>
					)}
					<button
						type="button"
						onClick={() => syncMutation.mutate()}
						disabled={syncMutation.isPending}
						className="btn-secondary"
					>
						<RefreshCw
							size={14}
							className={cn(syncMutation.isPending && "animate-spin")}
						/>
						{syncMutation.isPending ? "Sync läuft..." : "Jetzt synchronisieren"}
					</button>
				</div>
			</div>

			{/* Stats Cards */}
			<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
				<StatsCard
					label="Gesamt"
					value={stats?.total ?? 0}
					color="text-stone-700"
				/>
				<StatsCard
					label="Nicht zugeordnet"
					value={stats?.unlinked ?? 0}
					color={
						(stats?.unlinked ?? 0) > 0 ? "text-amber-600" : "text-green-600"
					}
				/>
				<StatsCard
					label="Offene Rechnungen"
					value={summary?.count ?? openInvoices.length}
					color="text-blue-600"
				/>
				<StatsCard
					label="Offener Betrag"
					value={formatCurrency(summary?.totalOpen ?? 0)}
					color={
						(summary?.overdue ?? 0) > 0 ? "text-red-600" : "text-stone-700"
					}
				/>
			</div>

			{/* Relink Result */}
			{relinkMutation.isSuccess && relinkMutation.data?.data && (
				<div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-700 flex items-center gap-2">
					<Check size={14} />
					{relinkMutation.data.data.message}
				</div>
			)}
			{relinkMutation.isError && (
				<div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex items-center gap-2">
					<AlertTriangle size={14} />
					Zuordnung fehlgeschlagen
				</div>
			)}

			{/* Tabs */}
			<div className="flex gap-1 border-b border-stone-200">
				{tabs.map(([key, label]) => (
					<button
						key={key}
						type="button"
						onClick={() => setActiveTab(key)}
						className={cn(
							"px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px",
							activeTab === key
								? "border-brand-600 text-brand-700"
								: "border-transparent text-stone-500 hover:text-stone-700",
						)}
					>
						{label}
					</button>
				))}
			</div>

			{/* Content */}
			{activeTab === "pipeline" && <PipelineSection />}
			{activeTab === "invoices" && <InvoicesSection invoices={openInvoices} />}
			{activeTab === "unlinked" && <UnlinkedSection vouchers={unlinked} />}
		</div>
	);
}

// ============================================================
// Stats Card
// ============================================================

function StatsCard({
	label,
	value,
	color,
}: {
	label: string;
	value: number | string;
	color: string;
}) {
	return (
		<div className="card !py-3 text-center">
			<div className={cn("text-xl font-display", color)}>{value}</div>
			<div className="text-xs text-stone-400">{label}</div>
		</div>
	);
}
